import { useDeferredValue, useMemo } from 'react';

import { useNavigate, useSearch } from '@tanstack/react-router';

import { auditLogsApi } from '@/api/queries/auditLogs';
import { auditApi } from '@/api/queries/workspace';
import type { AuditLog } from '@/api/types/audit';
import {
  Avatar,
  Button,
  Icon,
  IconButton,
  Pagination,
  Select,
  Tag,
  TagMore,
  colorFromKey,
  initialsOf,
} from '@/components/ui';
import { cn } from '@/utils/cn';

import type { AuditSearch } from '../index';
import { AuditDetailPanel } from './AuditDetailPanel';
import {
  ACTION_META,
  DAY_BUCKET_ORDER,
  actionMeta,
  dayBucket,
  formatHourMinute,
  isKnownAction,
  timeAgo,
  type AuditAction,
  type DayBucket,
} from './auditUtils';

const ACTION_FILTERS: { value: AuditAction | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'create', label: 'Create' },
  { value: 'update', label: 'Update' },
  { value: 'delete', label: 'Delete' },
  { value: 'consent', label: 'Consent' },
  { value: 'sync', label: 'Sync' },
];

export function AuditGrid() {
  const navigate = useNavigate({ from: '/audit-logs/' });
  const search = useSearch({ from: '/audit-logs/' });

  const deferred = useDeferredValue(search);
  const isPending = deferred !== search;
  const { data } = auditLogsApi.useList({
    page: deferred.page,
    pageSize: deferred.pageSize,
  });
  const { data: serverStats } = auditApi.useStats();

  const updateSearch = (next: Partial<AuditSearch>) => {
    navigate({ search: (prev) => ({ ...prev, ...next }) });
  };

  // Client-side filter against the current page. Backend supports server-side
  // action/actor filters too (the existing /audit endpoint accepts them); this
  // could be lifted server-side as a follow-up.
  const filtered = useMemo(() => {
    const q = (search.q ?? '').trim().toLowerCase();
    return (data.data ?? []).filter((event) => {
      if (search.action && event.change?.action?.toLowerCase() !== search.action) return false;
      if (search.actor && event.actor?.id !== search.actor) return false;
      if (q) {
        const ref = (event.docRef?.email || event.docRef?.personId || '').toLowerCase();
        const summary = (event.change?.summary || '').toLowerCase();
        const action = (event.change?.action || '').toLowerCase();
        if (!ref.includes(q) && !summary.includes(q) && !action.includes(q)) return false;
      }
      return true;
    });
  }, [data.data, search.q, search.action, search.actor]);

  // Group by day bucket.
  const grouped = useMemo(() => {
    const map = new Map<DayBucket, AuditLog[]>();
    for (const event of filtered) {
      const bucket = dayBucket(event.occurredAt);
      const list = map.get(bucket) ?? [];
      list.push(event);
      map.set(bucket, list);
    }
    return DAY_BUCKET_ORDER.filter((b) => map.has(b)).map((b) => ({ bucket: b, events: map.get(b)! }));
  }, [filtered]);

  // Stats come from the backend /audit/stats aggregation. Fall back to
  // a page-local computation if the endpoint isn't reachable yet (returns null).
  const stats = useMemo(() => {
    if (serverStats) {
      return {
        today: serverStats.today,
        week: serverStats.last7Days,
        deletes: serverStats.deletionsThisWeek,
        syncs: serverStats.syncEvents,
      };
    }
    const today = filtered.filter((e) => dayBucket(e.occurredAt) === 'Today').length;
    const week = filtered.filter((e) =>
      ['Today', 'Yesterday', 'Earlier this week'].includes(dayBucket(e.occurredAt)),
    ).length;
    const deletes = filtered.filter((e) => e.change?.action?.toLowerCase() === 'delete').length;
    const syncs = filtered.filter((e) => e.change?.action?.toLowerCase() === 'sync').length;
    return { today, week, deletes, syncs };
  }, [filtered, serverStats]);

  // Actor dropdown options derived from the current page (lifted server-side
  // would require a dedicated actors endpoint; the page-local list is fine
  // for the typical case of a small actor pool).
  const actorOptions = useMemo(() => {
    const set = new Set<string>();
    for (const event of data.data ?? []) {
      if (event.actor?.id) set.add(event.actor.id);
    }
    return [
      { value: '', label: 'All actors' },
      ...Array.from(set).sort().map((id) => ({ value: id, label: id })),
    ];
  }, [data.data]);

  const total = data.pagination?.total ?? 0;
  const selectedEvent = useMemo(
    () => (search.selected ? (data.data ?? []).find((e) => e.id === search.selected) : undefined),
    [data.data, search.selected],
  );

  const handleSelect = (event: AuditLog) => {
    updateSearch({ selected: event.id === search.selected ? undefined : event.id });
  };

  const handleOpenContact = (ref: string) => {
    // ref is currently a string id; in Phase 7 we'll map email refs to contact ids.
    navigate({ to: '/contacts/$id', params: { id: ref } });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }} aria-busy={isPending || undefined}>
      <header className="page-header">
        <div>
          <h1 className="page-title">Audit logs</h1>
          <div className="page-subtitle">
            {total.toLocaleString()} events · GDPR-compliant immutable record
          </div>
        </div>
        <div className="page-actions">
          <Button variant="secondary" leftIcon={<Icon name="calendar" size={13} />} disabled>
            Date range: All time
          </Button>
          <Button variant="secondary" leftIcon={<Icon name="download" size={13} />} disabled>
            Export CSV
          </Button>
        </div>
      </header>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 12,
          marginBottom: 16,
        }}
      >
        <StatCard
          label="Events today"
          value={stats.today}
          sub={serverStats ? 'workspace-wide' : 'this page'}
          tone="brand"
        />
        <StatCard
          label="Last 7 days"
          value={stats.week}
          sub={serverStats ? 'workspace-wide' : 'this page'}
        />
        <StatCard
          label="Deletions this week"
          value={stats.deletes}
          sub="all soft-deletes"
          tone="red"
        />
        <StatCard
          label="Sync events this week"
          value={stats.syncs}
          sub="external integrations"
          tone="teal"
        />
      </div>

      <div className="toolbar">
        <div className="search-input">
          <Icon name="search" size={13} className="si-icon" />
          <input
            value={search.q ?? ''}
            placeholder="Search by contact, field, value…"
            onChange={(e) =>
              updateSearch({ q: e.target.value || undefined, selected: undefined })
            }
            aria-label="Search audit events"
          />
        </div>

        <div className="audit-filter-group" role="tablist" aria-label="Action filter">
          {ACTION_FILTERS.map((f) => {
            const isActive =
              f.value === 'all' ? search.action === undefined : search.action === f.value;
            return (
              <button
                key={f.value}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={cn('audit-filter', isActive && 'active')}
                onClick={() =>
                  updateSearch({
                    action: f.value === 'all' ? undefined : (f.value as AuditAction),
                    selected: undefined,
                  })
                }
              >
                {f.label}
              </button>
            );
          })}
        </div>

        <Select
          value={search.actor ?? ''}
          onChange={(e) =>
            updateSearch({ actor: e.target.value || undefined, selected: undefined })
          }
          options={actorOptions}
          style={{ height: 28, width: 'auto', minWidth: 130, fontSize: 12 }}
          aria-label="Actor filter"
        />

        <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-3)' }}>
          {filtered.length} events on this page
        </span>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: selectedEvent ? '1fr 380px' : '1fr',
          gap: 14,
          alignItems: 'start',
        }}
      >
        <div
          className="table-wrap"
          style={{ borderRadius: '0 0 var(--radius-md) var(--radius-md)' }}
        >
          {grouped.length === 0 ? (
            <div className="table-empty">
              {isPending ? 'Loading...' : 'No events match your filters'}
            </div>
          ) : (
            grouped.map(({ bucket, events }) => (
              <div key={bucket}>
                <div className="audit-bucket">
                  {bucket}
                  <span className="audit-bucket-count">· {events.length} events</span>
                </div>
                {events.map((event) => (
                  <AuditRow
                    key={event.id}
                    event={event}
                    selected={search.selected === event.id}
                    onSelect={handleSelect}
                  />
                ))}
              </div>
            ))
          )}

          <Pagination
            page={search.page}
            pageSize={search.pageSize}
            total={total}
            onPageChange={(page) => updateSearch({ page })}
            onPageSizeChange={(pageSize) => updateSearch({ pageSize, page: 0 })}
          />
        </div>

        {selectedEvent && (
          <AuditDetailPanel
            event={selectedEvent}
            onClose={() => updateSearch({ selected: undefined })}
            onOpenContact={handleOpenContact}
            onFilterContact={(personId) => updateSearch({ q: personId, page: 0 })}
          />
        )}
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  sub,
  tone = 'default',
}: {
  label: string;
  value: number | string;
  sub?: string;
  tone?: 'default' | 'brand' | 'red' | 'teal';
}) {
  const color =
    tone === 'brand'
      ? 'var(--brand)'
      : tone === 'red'
      ? 'var(--red)'
      : tone === 'teal'
      ? 'var(--teal)'
      : 'var(--text)';
  return (
    <div className="stat-card">
      <div className="stat-label">{label}</div>
      <div className="stat-value" style={{ color }}>
        {value}
      </div>
      {sub && <div className="stat-sub">{sub}</div>}
    </div>
  );
}

function AuditRow({
  event,
  selected,
  onSelect,
}: {
  event: AuditLog;
  selected: boolean;
  onSelect: (event: AuditLog) => void;
}) {
  const meta = actionMeta(event.change?.action);
  const ref = event.docRef?.email || event.docRef?.personId || '';
  const actorId = event.actor?.id || 'system';
  const actorIsSystem = event.actor?.type === 'system';
  const versionFrom = event.change?.versionFrom ?? 0;
  const versionTo = event.change?.versionTo ?? 0;

  // The API exposes a single `summary` per event. Render it as a single chip
  // (or a +N tag if it's overly long); the detail panel shows the full text.
  const summaryText = event.change?.summary ?? '';
  const hasSummary = summaryText.length > 0;

  return (
    <div
      className={cn('audit-row', selected && 'selected')}
      role="button"
      tabIndex={0}
      onClick={() => onSelect(event)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(event);
        }
      }}
    >
      <div className="audit-time" title={new Date(event.occurredAt).toLocaleString()}>
        <div className="t-time">{formatHourMinute(event.occurredAt)}</div>
        <div className="t-ago">{timeAgo(event.occurredAt)}</div>
      </div>

      <div className="audit-action" style={{ color: meta.color }}>
        <span className="audit-action-dot" style={{ background: meta.color }} />
        <Icon name={meta.icon} size={12} />
        <span>{meta.label}</span>
      </div>

      <div className="audit-target">
        {ref ? (
          <>
            <Avatar initials={initialsOf(ref)} color={colorFromKey(ref)} size={22} />
            <div style={{ minWidth: 0 }}>
              <div className="t-name">{ref}</div>
              {event.docRef?.collection && (
                <div className="t-sub">{event.docRef.collection}</div>
              )}
            </div>
          </>
        ) : (
          <span style={{ color: 'var(--text-3)', fontStyle: 'italic', fontSize: 12 }}>
            System-wide
          </span>
        )}
      </div>

      <div className="audit-changes">
        {hasSummary ? (
          <span
            className="audit-change"
            title={summaryText}
            style={{ maxWidth: 360 }}
          >
            <span className="audit-field">{event.change?.action ?? 'event'}</span>{' '}
            <span style={{ color: 'var(--text-2)' }}>{summaryText}</span>
          </span>
        ) : (
          <Tag>—</Tag>
        )}
        {summaryText.length > 80 && <TagMore count={1} />}
      </div>

      <div className="audit-actor">
        <Avatar initials={initialsOf(actorId.split('@')[0])} color={colorFromKey(actorId)} size={22} />
        <div className="a-name" title={`${actorId}${event.actor?.ip ? ` · ${event.actor.ip}` : ''}`}>
          {actorId}
          {actorIsSystem && <span className="a-system">· system</span>}
        </div>
      </div>

      <div className="audit-version">
        {versionFrom !== versionTo ? (
          <span className="v-mono">
            v{versionFrom} → v{versionTo}
          </span>
        ) : isKnownAction(event.change?.action?.toLowerCase()) &&
          event.change?.action?.toLowerCase() === 'create' ? (
          <span className="v-mono fresh">v{versionTo}</span>
        ) : (
          <span style={{ color: 'var(--text-4)' }}>—</span>
        )}
      </div>

      <span style={{ color: 'var(--text-4)', display: 'inline-flex' }}>
        <Icon name="chevronRight" size={12} />
      </span>
    </div>
  );
}

// Re-export so the tree-shaker doesn't drop ACTION_META if needed externally.
export { ACTION_META };
